import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { selectLightweightElementsSlice } from "@/store/lightweight-elements-selectors";
import { LightweightElementsState } from "@/store/lightweight-elements-slice";
import { selectProjectFilteringSlice } from "@/store/project-filtering/project-filtering-selectors";
import { ProjectFilteringSlice } from "@/store/project-filtering/project-filtering-slice";
import { selectIsPanoExtractedFromData } from "@/store/project-selector";
import { curryAppSelector } from "@/store/reselect";
import {
  computeIsSheetVisible,
  selectAreaFor,
  selectAreaSections,
  selectSelectionsSlice,
  selectVisibleSheetsList,
} from "@/store/selections-selectors";
import { SelectionState, VisibleSheets } from "@/store/selections-slice";
import { RootState } from "@/store/store";
import {
  selectFilteredElementsWithTags,
  selectTagsSlice,
} from "@/store/tags/tags-selectors";
import type { TagsState } from "@/store/tags/tags-slice";
import { selectSheetOverrides } from "@/tools/create-report/create-report-selectors";
import {
  IElementOrthomosaic,
  IElementPhotogrammetryScan,
  isIElementOrthomosaic,
  isIElementPhotogrammetryScan,
} from "@/types/ielement-extra-types";
import { TreeData } from "@/utils/tree-generation/tree-data";
import {
  GUID,
  LARGEST_MILLISECONDS,
  SMALLEST_MILLISECONDS,
  assert,
  includes,
} from "@faro-lotv/foundation";
import {
  IElement,
  IElementGaussianSplats,
  IElementGenericAnnotation,
  IElementGenericImgSheet,
  IElementGenericOdometryPath,
  IElementGenericPointCloudStream,
  IElementImg2d,
  IElementImg360,
  IElementModel3dStream,
  IElementRoomSection,
  IElementSection,
  IElementTimeSeries,
  IElementType,
  IElementTypeHint,
  compareById,
  isIElementAreaSection,
  isIElementBimModelSection,
  isIElementGaussianSplats,
  isIElementGenericAnnotation,
  isIElementGenericDataSession,
  isIElementGenericImgSheet,
  isIElementGenericOdometryPath,
  isIElementGenericPointCloud,
  isIElementGenericPointCloudStream,
  isIElementImg2d,
  isIElementImg360,
  isIElementImg360GrayScale,
  isIElementMarkup,
  isIElementOdometryPathSection,
  isIElementOverviewImage,
  isIElementPanoInOdometryPath,
  isIElementPointCloudStream,
  isIElementRoomSection,
  isIElementSection,
  isIElementSectionFlash,
  isIElementSectionWithTypeHint,
  isIElementTimeTravelSection,
  isIElementWithTypeAndHint,
  isValid,
  pickClosestInTime,
} from "@faro-lotv/ielement-types";
import {
  IElementsState,
  newestToOldest,
  selectAncestor,
  selectChildDepthFirst,
  selectChildrenDepthFirst,
  selectDatasetOrArea,
  selectIElement,
  selectIElementChildren,
  selectIElementWorldPosition,
  selectIsDataSessionAligned,
  selectReferenceElement,
  selectSiblingPano,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import { uniqBy } from "es-toolkit";
import { DateTime, Interval } from "luxon";
import {
  CurrentAreaData,
  selectIsPointCloudViewable,
} from "./select-current-area-data";
import { selectCurrentSceneFiltered } from "./select-current-scene-filtered";

/**
 * The types allowed for the main model in the scene.
 */
export type MainModelElement =
  | IElementGenericPointCloudStream
  | IElementGaussianSplats
  | IElementImg360
  | IElementImg2d
  | IElementOrthomosaic;

/** The data necessary for the sheet mode to work properly */
export type CurrentScene = {
  /** Current element selected by the user */
  activeElement: IElement;
  /** The current dataset/rooms that contains the activeElement */
  referenceElement?: IElementSection;
  /** True if this scene should use intensity data if available */
  shouldUseIntensityData: boolean;
  /** The list of visible sheets */
  activeSheets: IElementGenericImgSheet[];
  /**
   * The single active sheet whose elevation and orientation should be used for navigating waypoints, panos, paths, etc.
   * The current specs are that all active sheets will have the same elevation.
   */
  activeSheetFor2DNavigation?: IElementGenericImgSheet;
  /** The best rendering object relative to the active element */
  main?: MainModelElement;
  /** The current cad model to render in the scene depending on the user selection */
  cad?: IElementModel3dStream;
  /** The trajectories to render together with the current scene for the user to navigate */
  paths: IElementGenericOdometryPath[];
  /** The list of rooms placeholders to show based on the current area and referenceElement */
  panos: IElementImg360[];
  /** The list of attached photos from photo only locations */
  photos: IElementImg2d[];
  /** The list of orthomosaics in the current scene */
  orthomosaics: IElementPhotogrammetryScan[];
  /** The list of point annotations in the current scene */
  annotations: IElementGenericAnnotation[];
  /** The list of sheets available for the current area and referenceElement */
  availableSheets: IElementGenericImgSheet[];
  /**
   * The highest and lowest placeholder altitude in the current scene.
   * Only panorama images from datasets are taken into account
   */
  waypointAltitudeRange?: WaypointRange;

  /** The oldest and newest creation date among all panos in the current scene. */
  waypointDateRange?: WaypointRange;

  /** The available location markers in the current scene*/
  locationMarkers: IElementRoomSection[];
};

/**
 * @returns True if the input element is not a 360 belonging to a path
 * @param element The input element
 */
function isNonTrajectoryPano(element: IElement): element is IElementImg360 {
  return (
    isIElementImg360(element) &&
    element.typeHint !== IElementTypeHint.odometryPath
  );
}

/**
 * Selects the best sheet for a given ielement.
 *
 * @param element A given element, or undefined
 * @returns The best sheet for the given element, or undefined
 */
export function selectSheetForElement(element: IElement | undefined) {
  return ({
    iElements,
  }: OnlyIElementsStore): IElementGenericImgSheet | undefined => {
    if (!element) return;
    const area = selectAreaFor(element)({ iElements });
    if (!area) {
      return;
    }
    return selectBestImgSheet(element, area)({ iElements });
  };
}

/**
 * @returns The first sheet child of the active element data session or area
 * @param activeElement The active element to check
 * @param area The area which the element belongs to
 */
function selectBestImgSheet(activeElement: IElement, area?: IElementSection) {
  return (state: OnlyIElementsStore): IElementGenericImgSheet | undefined => {
    const dataSessionOrFloor = selectDatasetOrArea(activeElement)(state);

    // Compute the current sheet, as the child of the data session or floor
    let sheet = selectChildDepthFirst(
      dataSessionOrFloor,
      isIElementGenericImgSheet,
    )(state);
    if (!sheet) {
      sheet = selectChildDepthFirst(area, isIElementGenericImgSheet)(state);
    }
    return sheet;
  };
}

// Filter out duplicated overview maps from the list found in the project.
// Overview maps are considered duplicated if they have the same associated file (i.e. md5Hash value).
// In case of duplicates, we keep the one that is part of the area, because the one belonging to the dataset
// is surely loaded later.
function filterUniqueOverviewMaps(
  sheets: IElementGenericImgSheet[],
  state: OnlyIElementsStore,
): IElementGenericImgSheet[] {
  const output: IElementGenericImgSheet[] = [];
  const map = new Map<string, IElementGenericImgSheet>();
  for (const sheet of sheets) {
    if (isIElementOverviewImage(sheet) && sheet.md5Hash) {
      let useThisSheet = true;
      if (map.has(sheet.md5Hash)) {
        // duplicated entry: ignore the one part of the dataset, because surely it is loaded later
        useThisSheet = !!selectAncestor(sheet, isIElementAreaSection)(state);
      }
      if (useThisSheet) {
        map.set(sheet.md5Hash, sheet);
      }
    } else {
      // No hash available or not an overview map = do not filter it out
      output.push(sheet);
    }
  }
  return output.concat(...map.values());
}

/**
 * A memoized selector that returns the set of project data needed to navigate a given area with a given
 * active element, a given tagging status and a given selection status.
 *
 * @param state The application store state
 * @param activeElement the currently active element
 * @param activeCad the currently active cad model
 * @param areaData the current area data
 * @param tree the current representation of the project capture tree
 * @param shouldUseIntensityData true to use intensity data if available for 360s. @default false
 * @returns the memoized selector mentioned above
 */
export const selectCurrentScene = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectSelectionsSlice,
      selectVisibleSheetsList,
      selectProjectFilteringSlice,
      selectTagsSlice,
      selectLightweightElementsSlice,
      selectSheetOverrides,
      (state: RootState, activeElement?: IElement) => activeElement,
      (
        state: RootState,
        activeElement?: IElement,
        activeCad?: IElementModel3dStream,
      ) => activeCad,
      (
        state: RootState,
        activeElement?: IElement,
        activeCad?: IElementModel3dStream,
        areaData?: CurrentAreaData,
      ) => areaData,
      (
        state: RootState,
        activeElement?: IElement,
        activeCad?: IElementModel3dStream,
        areaData?: CurrentAreaData,
        tree?: TreeData[],
      ) => tree,
      (
        state: RootState,
        activeElement?: IElement,
        activeCad?: IElementModel3dStream,
        areaData?: CurrentAreaData,
        tree?: TreeData[],
        shouldUseIntensityData: boolean = false,
      ) => shouldUseIntensityData,
    ],
    computeCurrentScene,
  ),
);

/**
 * @returns The set of data the user want to navigate in the current area based on the selected element
 * @param iElements The iElements store slice
 * @param selections The selection store slice
 * @param hasNewAreaNavigation True if the new area navigation feature flag is enabled
 * @param visibleSheets The list of visible sheets
 * @param projectFiltering The current project filtering parameters
 * @param tags The tags store slice
 * @param lightweightElements The current lightweight elements state
 * @param sheetOverrides Optional overrides for sheet filtering and selection
 * @param activeElement The current active element of the app
 * @param activeCad The current cad model selected by the user
 * @param areaData The available data in the current area
 * @param tree the current representation of the project capture tree
 * @param shouldUseIntensityData True to use intensity data if available for 360s
 */
function computeCurrentScene(
  iElements: IElementsState,
  selections: SelectionState,
  visibleSheets: VisibleSheets | undefined,
  projectFiltering: ProjectFilteringSlice,
  tags: TagsState,
  lightweightElements: LightweightElementsState,
  sheetOverrides: SheetOverrides | undefined,
  activeElement?: IElement,
  activeCad?: IElementModel3dStream,
  areaData?: CurrentAreaData,
  tree?: TreeData[],
  shouldUseIntensityData: boolean = false,
): CurrentScene | undefined {
  if (!activeElement || !areaData) {
    return;
  }

  const referenceElement = selectActiveElementReference(
    activeElement,
    areaData,
  )({ iElements });

  const { sheets, activeSheets } = selectActiveSheetsInScene(
    { iElements },
    areaData,
    referenceElement ?? activeElement,
    visibleSheets,
    sheetOverrides,
  );

  // We assume all sheets have the same elevation + use the first one for orientation
  const activeSheetFor2DNavigation = activeSheets[0];

  // Handle the case when the floor data is not yet loaded
  if (!referenceElement) {
    return {
      shouldUseIntensityData,
      activeSheets,
      activeSheetFor2DNavigation,
      paths: [],
      panos: [],
      photos: [],
      orthomosaics: [],
      activeElement,
      referenceElement,
      cad: activeCad,
      annotations: selectActiveAnnotations(
        { iElements, projectFiltering },
        areaData,
        activeCad,
      ),
      availableSheets: sheets,
      locationMarkers: areaData.locationMarkers,
    };
  }

  const {
    staticPanos,
    paths,
    photos,
    orthomosaics,
    waypointAltitudeRange,
    waypointDateRange,
  } = selectCurrentSceneStatic(
    { iElements },
    referenceElement,
    areaData,
    shouldUseIntensityData,
  );

  const {
    filteredPanos,
    filteredPhotos,
    filteredLocationMarkers,
    filteredPaths,
    filteredOrthomosaics,
  } = selectCurrentSceneFiltered(
    {
      iElements,
      selections,
      projectFiltering,
      lightweightElements,
      tags,
    },
    areaData,
    tree,
    staticPanos,
    paths,
    photos,
    orthomosaics,
  );

  return {
    shouldUseIntensityData,
    activeSheets,
    activeSheetFor2DNavigation,
    paths: filteredPaths,
    panos: filteredPanos,
    photos: filteredPhotos,
    orthomosaics: filteredOrthomosaics,
    activeElement,
    referenceElement,
    main: selectMainModel(
      activeElement,
      areaData,
      shouldUseIntensityData,
    )({ iElements }),
    cad: activeCad,
    annotations: selectActiveAnnotations(
      { iElements, projectFiltering },
      areaData,
      activeCad,
    ),
    availableSheets: sheets,
    waypointAltitudeRange,
    waypointDateRange,
    locationMarkers: filteredLocationMarkers,
  };
}

type ActiveSheetsInScene = {
  sheets: IElementGenericImgSheet[];
  activeSheets: IElementGenericImgSheet[];
};

/** Configuration for overriding the default sheet selection behavior */
export type SheetOverrides = {
  /** Filter to exclude certain sheets from both available and active sheets */
  sheetFilter(sheet: IElementGenericImgSheet): boolean;
  /** Map of area ID to the sheet ID that should be active */
  activeSheetByArea: Record<GUID, GUID>;
};

/**
 * Applies optional sheet overrides to the base sheet selection.
 *
 * @param base The base sheet selection from selectActiveSheetsInScene
 * @param overrides Optional overrides for filtering and per-area selection
 * @param areaId The current area ID, used to look up per-area sheet overrides
 * @returns The (possibly modified) sheet selection
 */
function applySheetOverrides(
  base: ActiveSheetsInScene,
  overrides: SheetOverrides | undefined,
  areaId: GUID | undefined,
): ActiveSheetsInScene {
  if (!overrides) return base;

  const sheets = base.sheets.filter(overrides.sheetFilter);
  let activeSheets = base.activeSheets.filter(overrides.sheetFilter);

  if (areaId) {
    const selectedSheetId = overrides.activeSheetByArea[areaId];
    if (selectedSheetId) {
      const selectedSheet = sheets.find((s) => s.id === selectedSheetId);
      if (selectedSheet) {
        activeSheets = [selectedSheet];
      }
    }
  }

  return { sheets, activeSheets };
}

const selectActiveSheetsInScene = createSelector(
  [
    selectIElementsSlice,
    (state: OnlyIElementsStore, areaData: CurrentAreaData) => areaData,
    (
      state: OnlyIElementsStore,
      areaData: CurrentAreaData,
      referenceElement: IElement,
    ) => referenceElement,
    (
      state: OnlyIElementsStore,
      areaData: CurrentAreaData,
      referenceElement: IElement,
      visibleSheets: VisibleSheets | undefined,
    ) => visibleSheets,
    (
      state: OnlyIElementsStore,
      areaData: CurrentAreaData,
      referenceElement: IElement,
      visibleSheets: VisibleSheets | undefined,
      sheetOverrides: SheetOverrides | undefined,
    ) => sheetOverrides,
  ],
  computeActiveSheetsInScene,
);

function computeActiveSheetsInScene(
  iElements: IElementsState,
  areaData: CurrentAreaData,
  referenceElement: IElement,
  visibleSheets: VisibleSheets | undefined,
  sheetOverrides: SheetOverrides | undefined,
): ActiveSheetsInScene {
  const allSheets = selectAllSheets(
    areaData.area,
    referenceElement,
  )({ iElements });

  // Remove duplicated overview maps (i.e. with same attached files) as they may appear twice in the project
  const sheets = filterUniqueOverviewMaps(allSheets, { iElements }).filter(
    (sheet) =>
      // This check could be improved once we will have a proper type or type hint
      // for the orthomosaic sheets
      !selectAncestor(sheet, isIElementPhotogrammetryScan)({ iElements }),
  );

  const bestSheet = selectBestImgSheet(
    referenceElement,
    areaData.area,
  )({ iElements });

  const newActiveSheets: IElementGenericImgSheet[] = [];
  // check which children are visible
  for (const sheet of sheets) {
    if (computeIsSheetVisible(visibleSheets, sheet.id)) {
      newActiveSheets.push(sheet);
    }
  }
  if (newActiveSheets.length === 0) {
    if (bestSheet && sheets.find((s) => s.id === bestSheet.id)) {
      newActiveSheets.push(bestSheet);
    }
  }

  return applySheetOverrides(
    {
      sheets,
      activeSheets: newActiveSheets,
    },
    sheetOverrides,
    areaData.area?.id,
  );
}

/** All data that should be rendered in the current scene, before filtering by tags and visibility */
type CurrentSceneStatic = {
  /** List of panos statically present in the scene */
  staticPanos: IElementImg360[];
  /** List of paths present in the scene */
  paths: IElementGenericOdometryPath[];
  /** List of 2D photos present in the scene */
  photos: IElementImg2d[];
  /** List of orthomosaics present in the scene */
  orthomosaics: IElementPhotogrammetryScan[];
  /** Range of altitudes for waypoints in the scene */
  waypointAltitudeRange: WaypointRange | undefined;
  /** Range of dates for waypoints in the scene */
  waypointDateRange: WaypointRange | undefined;
};

/**
 * This selector computes what is statically in the scene, meaning before tags filtering and visibility filtering.
 *
 * @returns the memoized selector
 * @param state the application store state
 * @param referenceElement the current reference element to navigate
 * @param areaData the current area data
 * @param shouldUseIntensityData true to use intensity data if available for 360s
 */
const selectCurrentSceneStatic = createSelector(
  [
    selectIElementsSlice,
    (state: OnlyIElementsStore, referenceElement: IElementSection) =>
      referenceElement,
    (
      state: OnlyIElementsStore,
      referenceElement: IElementSection,
      areaData: CurrentAreaData,
    ) => areaData,
    (
      state: OnlyIElementsStore,
      referenceElement: IElementSection,
      areaData: CurrentAreaData,
      shouldUseIntensityData: boolean,
    ) => shouldUseIntensityData,
  ],
  computeCurrentSceneStatic,
);

function computeCurrentSceneStatic(
  iElements: IElementsState,
  referenceElement: IElementSection,
  areaData: CurrentAreaData,
  shouldUseIntensityData: boolean,
): CurrentSceneStatic {
  const panos: IElementImg360[] = [];
  const photos: IElementImg2d[] = [];
  const orthomosaics: IElementPhotogrammetryScan[] = [];

  const paths = selectPathsForCurrentScene(
    { iElements },
    areaData,
    referenceElement,
  );

  const isDataSessionAligned = selectIsDataSessionAligned(referenceElement)({
    iElements,
  });

  // Use all 5d data sessions without any date-based filtering
  const dataSessions = areaData.dataSessions5d;

  // Collect 2d data inside 5d data sessions from all data sets that are aligned to the current one.
  const dataSessionsToShow = isDataSessionAligned
    ? dataSessions
    : [referenceElement];

  // Collect all 360s
  panos.push(
    ...dataSessionsToShow.flatMap((session) =>
      selectDataSet360s(session, false, shouldUseIntensityData)({ iElements }),
    ),
  );

  // Only add rooms, if their area is also currently shown.
  // Rooms are likely unaligned to each other. This prevents them being shown in the wrong location when the "entire project" is shown
  if (areaData.area) {
    const panoSections = areaData.roomsTimeSeries.map((timeSeries) =>
      selectClosestPanoSectionFromTimeSeries(
        referenceElement,
        timeSeries,
      )({ iElements }),
    );
    panos.push(
      ...panoSections
        .flatMap((panoSection) =>
          selectChildDepthFirst(panoSection, isIElementImg360)({ iElements }),
        )
        .filter(isValid),
    );

    const areaImgs = selectImgsFromRooms(areaData.roomsWithImg2d)({
      iElements,
    });
    photos.push(...areaImgs);

    // Collect the orthomosaic
    const photogrammetryScan = selectChildDepthFirst(
      referenceElement,
      isIElementPhotogrammetryScan,
    )({ iElements });
    if (photogrammetryScan) {
      const sheet = selectChildDepthFirst(
        photogrammetryScan,
        isIElementOrthomosaic,
      )({ iElements });
      if (sheet) {
        orthomosaics.push(photogrammetryScan);
      }
    }
  }

  // Collect 2d data from 2d only data sessions
  for (const session of areaData.dataSessions2d) {
    const sessionPanos = selectDataSet360s(
      session,
      true,
      shouldUseIntensityData,
    )({ iElements });
    const nonPathPanos = sessionPanos.filter(isNonTrajectoryPano);

    // There can be 2d Orbis data sets with paths and flash scans, so this needs to be a separate condition.
    // Panos should also not be filtered by date, to allow Focus scans only datasets to work as rooms.
    if (nonPathPanos.length) {
      panos.push(...nonPathPanos);
    }
  }

  // Filter out panos that are part of the odometry path
  const staticPanos = panos.filter(
    (pano) =>
      !selectAncestor(pano, isIElementOdometryPathSection)({ iElements }),
  );

  // Find the highest and lowest waypoint in the current scene
  const waypointAltitudeRange = selectPanoAltitudeRange(staticPanos)({
    iElements,
  });

  // Find the oldest and most recent waypoints in the current scene
  const waypointDateRange = computePanoDateRange(staticPanos);

  return {
    staticPanos,
    paths,
    photos,
    orthomosaics,
    waypointAltitudeRange,
    waypointDateRange,
  };
}

/**
 * @param annotation reference element to start the search
 * @returns the markup element that contains the tags, which is a child of the annotation
 */
function selectAnnotationTagReference(annotation: IElementGenericAnnotation) {
  return (state: OnlyIElementsStore) =>
    selectChildDepthFirst(annotation, isIElementMarkup, 1)(state);
}

/**
 * @param el to check
 * @returns true if it's one of the main model we can render (360, 2D photo, orthomosaic, gs, pc or cad)
 */
export function isMainModel(el: IElement): el is MainModelElement {
  return (
    isIElementImg360(el) ||
    isIElementImg2d(el) ||
    isIElementGaussianSplats(el) ||
    isIElementGenericPointCloudStream(el) ||
    isIElementOrthomosaic(el)
  );
}

/**
 * @param el to check
 * @returns true if it's a main model that is also viewable
 */
function isViewableMainModel(el: IElement) {
  return (state: OnlyIElementsStore) =>
    isMainModel(el) &&
    (!isIElementGenericPointCloudStream(el) ||
      selectIsPointCloudViewable(el)(state));
}

/**
 * @returns what main model the user is looking at (PC, Cad, GS or 360)
 * @param activeElement current element selected by the user
 * @param areaData current data for the active area
 * @param shouldUseIntensityData true to prefer the intensity image variant of an img360
 */
function selectMainModel(
  activeElement: IElement | undefined,
  areaData: CurrentAreaData,
  shouldUseIntensityData: boolean,
) {
  return (state: OnlyIElementsStore): MainModelElement | undefined => {
    if (!activeElement) return;

    if (isMainModel(activeElement)) {
      // If more than one 360s is available for the current scan prefer the one
      // with the current color type used in this scene
      if (isIElementImg360(activeElement)) {
        const variants = selectPanoColorVariants(activeElement)(state);
        if (
          variants.differentTypeSiblingPano &&
          isIElementImg360GrayScale(activeElement) !== shouldUseIntensityData
        ) {
          return variants.differentTypeSiblingPano;
        }
      }
      return activeElement;
    }

    const models = [...areaData.dataSessions, ...areaData.roomsSections];

    // Check if the current element is a data set with a point cloud, so we render that point cloud
    const dataSet = selectAncestor(activeElement, (el) =>
      includes(models, el, compareById),
    )(state);
    if (dataSet) {
      // Data sessions need only 2 levels, but videomode datasets require 4 levels
      const maxSearchDepth = 4;
      return selectChildDepthFirst(
        dataSet,
        (el): el is MainModelElement => isViewableMainModel(el)(state),
        maxSearchDepth,
      )(state);
    }
  };
}

/**
 * @param state the state of the app store
 * @param activeElement to compute the reference element for
 * @param areaData all the data in the current area
 * @returns the reference element (the one containing time information) from the active element if it exists
 */
export const selectActiveElementReference = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, activeElement: IElement | undefined) =>
        activeElement,
      (
        state: OnlyIElementsStore,
        activeElement: IElement | undefined,
        areaData: CurrentAreaData,
      ) => areaData,
    ],
    computeActiveElementReference,
  ),
);

function computeActiveElementReference(
  iElements: IElementsState,
  activeElement: IElement | undefined,
  areaData: CurrentAreaData,
): IElementSection | undefined {
  if (!activeElement) return;
  // All the possible elements that can be used as a time reference (data sessions, rooms, or bim models)
  // Maps to what the user can select in the project tree or in the various data selections uis (Eg. TimeDropDown)
  const referenceGroup = [
    ...(areaData.area ? [areaData.area] : []),
    ...areaData.scans,
    ...[
      ...areaData.dataSessions,
      ...areaData.dataSessions2d,
      ...areaData.roomsSections,
    ].sort(newestToOldest),
  ];

  const state = { iElements };

  // Check if the activeElement is part of one of the reference element
  let reference = selectAncestor(activeElement, (el) =>
    includes(referenceGroup, el, compareById),
  )(state);
  if (reference && isIElementSection(reference)) {
    return reference;
  }

  // Here the active element is outside the reference group, find the newest reference element that is contained
  // in the active element (To handle the user selecting an Area, or a Time Series)
  reference = referenceGroup.find((ref) =>
    selectAncestor(ref, (ancestor) => ancestor.id === activeElement.id)(state),
  );
  if (reference && isIElementSection(reference)) {
    return reference;
  }

  // for location marker, there is no time point info yet. Use itself as the reference.
  if (
    isIElementRoomSection(activeElement) &&
    areaData.locationMarkers.find((marker) => marker.id === activeElement.id)
  ) {
    return activeElement;
  }

  // for 2D photo, use parent location as reference element
  if (isIElementImg2d(activeElement)) {
    return selectAncestor(activeElement, isIElementRoomSection)(state);
  }
  // Pick the closest in time data session to the activeElement
  return selectReferenceElement(activeElement, referenceGroup)(state);
}

/**
 * This selector should be used when we want to find the room section, that is used to describe a collection of 360s. (i.e. a waypoint)
 * A simple isSectionRoom() lookup is not sufficient here, as we have a duplication of the "Section/Room" element type for the first element
 * of the TimeSeries. For example, this is a room with 3 pano sections:
 *
 * Section/Room
 * └ TimeSeries/Room
 *   ├ Section/Room         (contains first pano)
 *   ├ Section/TimeTravel   (contains second pano)
 *   └ Section/TimeTravel   (contains third pano)
 *
 * History lesson: This construct comes from a conversion of the legacy HB data model, where the same 3 panorama room does not have a
 * dedicated container element, but is a room pano with two time travels attached. The project API takes care of converting
 * between those structures.
 *
 * Room                     (contains first pano)
 * ├ TimeTravel             (contains second pano)
 * └ TimeTravel             (contains third pano)
 *
 * @param _state Current store state
 * @param targetElement The element to start a search from
 * @returns the outer ancestor room section of the active element, that is under a rooms group.
 */
export const selectOuterRoomSectionAncestor = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (_state: RootState, targetElement: IElement | undefined) => targetElement,
    ],
    (iElements: IElementsState, targetElement: IElement | undefined) =>
      selectAncestor(targetElement, (el) => {
        if (!isIElementRoomSection(el)) return false;

        const parent = selectIElement(el.parentId)({ iElements });

        return (
          !!parent &&
          isIElementWithTypeAndHint(
            parent,
            IElementType.group,
            IElementTypeHint.rooms,
          )
        );
      })({ iElements }),
  ),
);

/**
 * @param session to check
 * @param allowTrajectories true to allow trajectories panos
 * @param shouldUseIntensityData true to use intensity panos if available
 * @returns the representative 360s contained in the session, favoring color or grayscale ones over the other
 * based on the selected preference by the user
 */
export function selectDataSet360s(
  session: IElement,
  allowTrajectories: boolean,
  shouldUseIntensityData: boolean,
) {
  return (state: OnlyIElementsStore): IElementImg360[] => {
    const panos = selectChildrenDepthFirst(
      session,
      allowTrajectories ? isIElementImg360 : isNonTrajectoryPano,
    )(state);

    const typeHint = shouldUseIntensityData ? IElementTypeHint.grayScale : null;

    const byParent = new Map<GUID, IElementImg360[]>();
    for (const pano of panos) {
      const { parentId } = pano;
      assert(parentId, "Pano should have a parentId");
      if (byParent.has(parentId)) {
        byParent.get(parentId)?.push(pano);
      } else {
        byParent.set(parentId, [pano]);
      }
    }
    const ret: IElementImg360[] = [];
    for (const siblingPanos of byParent.values()) {
      let foundPanoWithHint = false;
      for (const pano of siblingPanos) {
        if (pano.typeHint === typeHint) {
          ret.push(pano);
          foundPanoWithHint = true;
        }
      }
      if (!foundPanoWithHint) {
        ret.push(...siblingPanos);
      }
    }
    return ret;
  };
}

type IsPanoWithIntensity = {
  /** Flag to know if the element is a panorama image with intensity. If false then the element is a colored pano. */
  isPanoWithIntensity?: boolean;

  /** Sibling panorama image of the passed element. It has a different typeHint from the passed element. */
  differentTypeSiblingPano?: IElementImg360;
};

/**
 * @param activeElement reference element to check
 * @returns an object containing a flag to know if the passed element is a color or grayscale pano,
 * and the id of the other color version available for that pano.
 */
export function selectPanoColorVariants(activeElement?: IElement) {
  return (state: OnlyIElementsStore): IsPanoWithIntensity => {
    if (!activeElement || !isIElementImg360(activeElement)) {
      return {};
    }

    const siblingPano = selectSiblingPano(
      activeElement,
      (el) =>
        el.typeHint !== activeElement.typeHint &&
        !isIElementPanoInOdometryPath(el),
    )(state);

    return {
      isPanoWithIntensity: isIElementImg360GrayScale(activeElement),
      differentTypeSiblingPano: siblingPano,
    };
  };
}

/**
 * @returns the annotations for the area, coming from the bi-tree or from the datasets
 * @param state current state of app store
 * @param area to compute the annotations for
 * @param activeCad active cad model to search associated annotation
 */
const selectActiveAnnotations = createSelector(
  [
    selectIElementsSlice,
    selectProjectFilteringSlice,
    (state: OnlyIElementsStore, area: CurrentAreaData) => area,
    (
      state: OnlyIElementsStore,
      area: CurrentAreaData,
      activeCad?: IElementModel3dStream,
    ) => activeCad,
  ],
  computeActiveAnnotations,
);

function computeActiveAnnotations(
  iElements: IElementsState,
  projectFiltering: ProjectFilteringSlice,
  area: CurrentAreaData,
  activeCad?: IElementModel3dStream,
): IElementGenericAnnotation[] {
  const areaRoots = area.area ? [area.area] : selectAreaSections({ iElements });

  const activeCadModleSection = selectAncestor(
    activeCad,
    isIElementBimModelSection,
  )({ iElements });

  // Find datasets that are not inside the area
  const captureTreeDataSets = area.dataSessions.filter(
    (element) =>
      !selectAncestor(
        element,
        ({ id }) => !!areaRoots.find((e) => e.id === id),
      )({ iElements }),
  );
  const annotationsRoots = [...areaRoots, ...captureTreeDataSets];
  if (activeCadModleSection) annotationsRoots.push(activeCadModleSection);
  const annotations = annotationsRoots
    .map((element) =>
      selectChildrenDepthFirst(
        element,
        isIElementGenericAnnotation,
      )({ iElements }),
    )
    .flat();

  return selectFilteredElementsWithTags(
    annotations,
    selectAnnotationTagReference,
  )({ iElements, projectFiltering });
}

/**
 * @returns All the sheets children of the reference element or the area.
 * @param state current store state
 * @param area The area to which the reference element belongs
 * @param referenceElement The reference element
 */
export const selectAllSheets = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, area?: IElementSection) => area,
      (
        state: OnlyIElementsStore,
        area?: IElementSection,
        referenceElement?: IElement,
      ) => referenceElement,
    ],
    computeAllSheets,
  ),
);

function computeAllSheets(
  iElements: IElementsState,
  area?: IElementSection,
  referenceElement?: IElement,
): IElementGenericImgSheet[] {
  const state = { iElements };
  // Get all sheets children of the reference element
  const sheets = selectChildrenDepthFirst(
    referenceElement,
    isIElementGenericImgSheet,
  )(state);
  // Get all sheets direct children of the area group
  const areaGroup = selectChildDepthFirst(area, (el) =>
    isIElementWithTypeAndHint(el, IElementType.group, IElementTypeHint.area),
  )(state);
  sheets.push(
    ...selectChildrenDepthFirst(areaGroup, isIElementGenericImgSheet)(state),
  );
  return uniqBy(sheets, (el) => el.id);
}

/**
 * @returns whether a given IElement is a viewable point cloud stream
 * @param element the IElement to check
 */
export function selectIsElementViewablePointCloudStream(element?: IElement) {
  return (state: OnlyIElementsStore) =>
    !!element &&
    isIElementPointCloudStream(element) &&
    selectIsPointCloudViewable(element)(state);
}

/**
 * @returns whether a given IElement is a viewable in the Viewer, or whether it should be hidden from the user completely.
 * @param element the IElement to check
 */
export function selectIsElementViewable(element?: IElement) {
  return (state: OnlyIElementsStore) => {
    if (!element) return false;

    if (
      isIElementGenericPointCloudStream(element) ||
      isIElementGenericPointCloud(element)
    ) {
      return selectIsPointCloudViewable(element)(state);
    }

    return true;
  };
}

export type WaypointRange = {
  /** The lowest(y) waypoint in the current scene */
  lowest: number;

  /** The highest(y) waypoint in the current scene */
  highest: number;
};

/**
 * Only panos that are coming from a dataset will be used to calculate the altitude range.
 * At least two valid panos are required to calculate the range.
 * Return a valid range only if the altitude difference is at least 1 meter.
 *
 * @param panos The list of pano images to consider for the range
 * @returns The highest and lowest panos in the list or undefined if no valid range is available
 */
function selectPanoAltitudeRange(panos?: IElementImg360[]) {
  return (state: OnlyIElementsStore): WaypointRange | undefined => {
    const MIN_VALID_RANGE = 1;
    // Take into account for the range only the panos, from datasets, that have a valid position
    const filteredPanos = panos?.filter((pano) =>
      selectIsPanoExtractedFromData(pano)(state),
    );

    // Skip the calculation if there are not enough panos to calculate the range
    if (!filteredPanos?.length || filteredPanos.length < 2) return;

    const range = filteredPanos.reduce(
      (prev, pano) => {
        const position = selectIElementWorldPosition(pano.id)(state);
        if (position[1] < prev.lowest) {
          prev.lowest = position[1];
        }
        if (position[1] > prev.highest) {
          prev.highest = position[1];
        }
        return prev;
      },
      {
        lowest: Number.POSITIVE_INFINITY,
        highest: Number.NEGATIVE_INFINITY,
      },
    );
    if (range.highest - range.lowest < MIN_VALID_RANGE) return;
    return range;
  };
}

/**
 *
 * @param panos Panos of the current scene
 * @returns Capture date interval for all panos
 */
function computePanoDateRange(
  panos?: IElementImg360[],
): WaypointRange | undefined {
  if (!panos) return;

  // Compute the oldest and the newest capture date of the panos
  const { oldest, newest } = panos.reduce(
    ({ oldest, newest }, { createdAt }) => {
      const date = DateTime.fromISO(createdAt);
      if (!date.isValid) return { oldest, newest };
      oldest = DateTime.min(oldest, date);
      newest = DateTime.max(newest, date);
      return { oldest, newest };
    },
    {
      oldest: DateTime.fromMillis(LARGEST_MILLISECONDS),
      newest: DateTime.fromMillis(SMALLEST_MILLISECONDS),
    },
  );

  // A date range is valid only if the difference between the oldest and newest date is at least 1 day
  const interval = Interval.fromDateTimes(oldest, newest);
  if (
    !interval.isValid ||
    interval
      .toDuration()
      .shiftTo("days", "hours", "minutes", "seconds", "milliseconds").days === 0
  ) {
    return;
  }

  return { lowest: oldest.toMillis(), highest: newest.toMillis() };
}

/**
 *
 * @returns Select from a timeseries, the closest in time section containing an img360
 * @param referenceElement The element used as reference for computing the distance in time
 * @param timeSeries The timeseries from which we want to obtain the pano section
 */
function selectClosestPanoSectionFromTimeSeries(
  referenceElement: IElement,
  timeSeries: IElementTimeSeries,
) {
  return (state: OnlyIElementsStore) =>
    pickClosestInTime(
      referenceElement,
      selectIElementChildren(timeSeries.id)(state)
        .filter(isIElementSection)
        // We want only the pano sections children of the room time series
        // that contain img360, and not e.g. ImgCube or other objects that
        // the viewer cannot visualize.
        .filter(
          (e: IElementSection) =>
            !!selectChildDepthFirst(e, isIElementImg360)(state),
        ),
    );
}

/**
 *
 * @returns Select newest 2D photos from given rooms
 * @param rooms the given rooms
 */
export function selectImgsFromRooms(rooms: IElementRoomSection[]) {
  return ({ iElements }: OnlyIElementsStore) =>
    rooms
      .map((room) => {
        const imgs = selectImgsFromRoom(room)({ iElements });
        if (imgs.length) return imgs[0];
        return null;
      })
      .filter((element) => element !== null);
}

/**
 *
 * @returns Select attached 2D photos from a room section
 * @param room the given room section
 */
export function selectImgsFromRoom(room: IElementRoomSection) {
  return ({ iElements }: OnlyIElementsStore) => {
    const photoContainer = selectChildDepthFirst(
      room,
      (el) =>
        isIElementWithTypeAndHint(
          el,
          IElementType.group,
          IElementTypeHint.attachments,
        ),
      1,
    )({ iElements });

    return selectChildrenDepthFirst(
      photoContainer,
      isIElementImg2d,
      1,
    )({ iElements }).sort(newestToOldest);
  };
}

const selectPathsForCurrentScene = createSelector(
  [
    selectIElementsSlice,
    (iElements: OnlyIElementsStore, areaData: CurrentAreaData) => areaData,
    (
      iElements: OnlyIElementsStore,
      areaData: CurrentAreaData,
      referenceElement: IElementSection,
    ) => referenceElement,
  ],
  (iElements, areaData, referenceElement) => {
    const paths: IElementGenericOdometryPath[] = [];

    // Collect the paths for the current active element.
    // - if the reference is a mobile scan, we want to collect the child path
    // - if the reference is a data session, we want to collect all paths in the data session
    // - if the reference is a room, we want to collect the path if it's a video mode location
    // - if the reference is an area, we want to collect all paths in the area
    // - if the reference is a flash scan, we want to collect the Orbis trajectory it was obtained from
    if (
      isIElementGenericDataSession(referenceElement) ||
      isIElementSectionWithTypeHint(
        referenceElement,
        IElementTypeHint.orbisScan,
      ) ||
      isIElementSectionWithTypeHint(referenceElement, IElementTypeHint.roviScan)
    ) {
      paths.push(
        ...selectChildrenDepthFirst(
          referenceElement,
          isIElementGenericOdometryPath,
        )({ iElements }),
      );
    } else if (
      isIElementRoomSection(referenceElement) ||
      isIElementTimeTravelSection(referenceElement)
    ) {
      let trajectoryFound = false;
      for (const session of areaData.dataSessions2d) {
        if (trajectoryFound) {
          break;
        }
        const sessionPaths = selectChildrenDepthFirst(
          session,
          isIElementGenericOdometryPath,
        )({ iElements });
        for (const path of sessionPaths) {
          if (trajectoryFound) {
            break;
          }
          const videoModeMatch = selectChildDepthFirst(
            path,
            (e) => isIElementImg360(e) && e.targetId === referenceElement.id,
          )({ iElements });
          if (videoModeMatch) {
            paths.push(path);
            trajectoryFound = true;
          }
        }
      }
    } else if (isIElementAreaSection(referenceElement)) {
      for (const session of [
        ...areaData.dataSessions2d,
        ...areaData.dataSessions5d,
      ]) {
        paths.push(
          ...selectChildrenDepthFirst(
            session,
            isIElementGenericOdometryPath,
          )({ iElements }),
        );
      }
    } else if (isIElementSectionFlash(referenceElement)) {
      const parent = referenceElement.parentId
        ? selectIElement(referenceElement.parentId)({ iElements })
        : undefined;
      if (parent) {
        const path = selectChildDepthFirst(
          parent,
          isIElementGenericOdometryPath,
        )({ iElements });
        if (path) {
          paths.push(path);
        }
      }
    }

    return paths;
  },
);
